import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

export default function DiscountCodeManager() {
  const { request } = useRequest();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  const fetchRows = async () => {
    setLoading(true);
    await request({
      url: apiPath.authAdminDiscountCodes,
      method: "GET",
      onSuccess: (data) => setItems(Array.isArray(data?.items) ? data.items : []),
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to load discount codes", Severty.ERROR);
      },
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      discountType: "percent",
      duration: "once",
      isActive: true,
      currency: "gbp",
    });
    setModalOpen(true);
  };

  const openToggle = (record) => {
    setEditingItem(record);
    form.resetFields();
    form.setFieldsValue({
      name: record?.name || "",
      isActive: record?.isActive !== false,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editingItem) {
        await request({
          url: `${apiPath.authAdminDiscountCodes}/${editingItem._id}`,
          method: "PATCH",
          data: {
            name: values.name || "",
            isActive: values.isActive !== false,
          },
          onSuccess: () => {
            ShowToast("Discount code updated", Severty.SUCCESS);
            setModalOpen(false);
            fetchRows();
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.message || "Update failed", Severty.ERROR);
          },
        });
      } else {
        await request({
          url: apiPath.authAdminDiscountCodes,
          method: "POST",
          data: values,
          onSuccess: () => {
            ShowToast("Discount code created", Severty.SUCCESS);
            setModalOpen(false);
            fetchRows();
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.message || "Create failed", Severty.ERROR);
          },
        });
      }
    } catch {
      /* validation */
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: "Code", dataIndex: "code", key: "code" },
    { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "—" },
    {
      title: "Discount",
      key: "discount",
      render: (_, row) =>
        row.discountType === "percent" ? `${row.amount}% off` : `\u00A3${Number(row.amount || 0).toFixed(2)} off`,
    },
    { title: "Duration", dataIndex: "duration", key: "duration" },
    {
      title: "Uses",
      key: "uses",
      render: (_, row) => `${row.timesRedeemed || 0}${row.maxUses ? ` / ${row.maxUses}` : ""}`,
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) => (
        <Tag color={row.isActive !== false ? "green" : "default"}>
          {row.isActive !== false ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Button onClick={() => openToggle(row)}>
          {row.isActive !== false ? "Manage" : "Review"}
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="Discount Codes"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Discount Code
        </Button>
      }
    >
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        scroll={{ x: "max-content" }}
        dataSource={items}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingItem ? "Manage Discount Code" : "New Discount Code"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {editingItem ? (
            <>
              <Form.Item name="name" label="Internal name">
                <Input placeholder="Optional label" />
              </Form.Item>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="code" label="Code" rules={[{ required: true, message: "Enter a code" }]}>
                <Input placeholder="e.g. FOUNDING25" />
              </Form.Item>
              <Form.Item name="name" label="Internal name">
                <Input placeholder="Optional label" />
              </Form.Item>
              <Space style={{ display: "flex" }} size={12}>
                <Form.Item
                  name="discountType"
                  label="Type"
                  rules={[{ required: true, message: "Select type" }]}
                  style={{ flex: 1 }}
                >
                  <Select
                    options={[
                      { label: "Percent", value: "percent" },
                      { label: "Fixed amount", value: "fixed" },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="amount"
                  label="Amount"
                  rules={[{ required: true, message: "Enter amount" }]}
                  style={{ flex: 1 }}
                >
                  <InputNumber style={{ width: "100%" }} min={0.01} />
                </Form.Item>
              </Space>
              <Space style={{ display: "flex" }} size={12}>
                <Form.Item name="duration" label="Duration" style={{ flex: 1 }}>
                  <Select
                    options={[
                      { label: "Once", value: "once" },
                      { label: "Repeating", value: "repeating" },
                      { label: "Forever", value: "forever" },
                    ]}
                  />
                </Form.Item>
                <Form.Item name="durationInMonths" label="Months" style={{ flex: 1 }}>
                  <InputNumber style={{ width: "100%" }} min={1} />
                </Form.Item>
              </Space>
              <Space style={{ display: "flex" }} size={12}>
                <Form.Item name="maxUses" label="Max uses" style={{ flex: 1 }}>
                  <InputNumber style={{ width: "100%" }} min={1} />
                </Form.Item>
                <Form.Item name="currency" label="Currency" style={{ flex: 1 }}>
                  <Select options={[{ label: "GBP", value: "gbp" }]} />
                </Form.Item>
              </Space>
            </>
          )}
        </Form>
      </Modal>
    </Card>
  );
}
